import { Location } from '@angular/common';
import {
    Component,
    Input,
    OnChanges,
    OnDestroy,
    ViewChild,
    OnInit,
    TemplateRef,
    ViewContainerRef,
    Output,
    EventEmitter,
    CUSTOM_ELEMENTS_SCHEMA,
    HostListener,
} from '@angular/core';
import { ActivatedRoute, Route, Router } from '@angular/router';
import { QueryService } from '@app/core/graphql/query/query.service';
import { SearchActions } from '@app/core/ngrx/actions';
import { State } from '@app/core/ngrx/reducers';
import { searchSelectors, userSelectors } from '@app/core/ngrx/selectors';
import { ProgramService } from '@app/core/services/program/program.service';
import { ToolService } from '@app/core/services/tool/tool.service';
import Rotation from '@Interfaces/Rotation';
import SearchItem from '@Interfaces/SearchItem';
import { Store } from '@ngrx/store';
import { NavController, Platform } from '@ionic/angular';
import { IonSlides } from '@ionic/angular';
import { fromEvent, Subscription } from 'rxjs';
import { filter, flatMap, take } from 'rxjs/operators';
import { Angulartics2GoogleTagManager } from 'angulartics2';
import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { IonicModule } from '@ionic/angular';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { findSingleQuickActionIndex } from '@app/modules/program/utils/quick-action-match';

@Component({
    selector: 'app-program-search-quick-actions',
    templateUrl: './program-search-quick-actions.component.html',
    styleUrls: ['./program-search-quick-actions.component.scss'],
    providers: [Angulartics2GoogleTagManager],
    standalone: true,
    imports: [CommonModule, FormsModule, ReactiveFormsModule, IonicModule],
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class ProgramSearchQuickActionsComponent implements OnInit, OnChanges, OnDestroy {
    @Input() programs?: Rotation[];
    @Input() data?: any;
    @Input() user?: any;
    @Input() filters: any;
    @Input() loading: any;
    @ViewChild('slides') slides: IonSlides;
    @Output() selection = new EventEmitter<any>();

    categories: any;
    showSuggested$ = this.store.select(searchSelectors.getShowSuggested);
    showMap$ = this.toolService.getShowMap();
    lastClicked: any = null;
    categoryButtonList: any = {};

    slideDesktopOpts = {
        slidesPerView: 5,
        centeredSlides: false,
        grabCursor: true,
    };

    slideMobileOpts = {
        slidesPerView: 'auto',
        centeredSlides: false,
        grabCursor: true,
        freeMode: true,
        freeModeSticky: false,
    };

    order: string[] = ['price-asc'];
    private lastSyncedParams: any = null;

    syncCategoryFillsFromQueryParams(params: any): void {
        this.lastSyncedParams = params;
        if (!this.categories || !this.categories.length) return;

        Object.keys(this.categories).forEach((i) => (this.categories[i].fill = 'solid'));
        const idxFromParams = findSingleQuickActionIndex(params, this.categories || []);
        const idxFromService = this.toolService.quickActionIndex.getValue();

        if (idxFromService !== null && idxFromParams !== null && idxFromParams !== idxFromService) {
            this.toolService.quickActionIndex.next(null);
        }

        const idx = idxFromService !== null ? idxFromService : idxFromParams;

        if (idx != null) {
            this.lastClicked = idx;
            this.categories[idx].fill = 'outline';
        } else {
            this.lastClicked = null;
        }
    }

    overlayRef: OverlayRef | null; // right click menu check
    @ViewChild('navMenu') navMenu: TemplateRef<any>; // ng template for right click menu
    sub: Subscription;
    private resetCategoriesSub: Subscription;
    private contentQuerySub?: Subscription;

    private readonly mobileBreakpoint = 992;
    isMobile = false;

    constructor(
        private toolService: ToolService,
        private queryService: QueryService,
        private location: Location,
        private navCtrl: NavController,
        private programService: ProgramService,
        private route: ActivatedRoute,
        private router: Router,
        private store: Store<State>,
        public overlay: Overlay,
        public viewContainerRef: ViewContainerRef,
        private angulartics2GoogleTagManager: Angulartics2GoogleTagManager,
        private platform: Platform
    ) {}

    @HostListener('window:resize')
    onResize() {
        this.isMobile = this.platform.width() < this.mobileBreakpoint;
    }

    ngOnInit() {
        this.isMobile = this.platform.width() < this.mobileBreakpoint;
        this.contentQuerySub = this.toolService.content
            .pipe(
                flatMap((textcontent: any) => {
                    this.categories = textcontent.content.categories;
                    if (this.lastSyncedParams) {
                        this.syncCategoryFillsFromQueryParams(this.lastSyncedParams);
                    }
                    return this.route.queryParams;
                })
            )
            .subscribe((params: any) => {
                this.lastSyncedParams = params;
                this.syncCategoryFillsFromQueryParams(params);
            });

        // Single subscription for filter reset — avoids accumulation from ngOnChanges
        this.resetCategoriesSub = this.programService.getResetCatagoriesSubject().subscribe((res: any) => {
            if (res) {
                this.lastClicked = null;
                setTimeout(() => {
                    this.categories?.forEach((element: any) => {
                        element.fill = 'solid';
                    });
                }, 500);
                this.programService.setResetCatagoriesSubject.next(false);
            }
        });

        // trigger listeners
        this.listeners();
    }

    ngOnChanges(): void {
        // intentionally empty — reset logic moved to ngOnInit to avoid subscription leaks
    }

    ngOnDestroy(): void {
        this.contentQuerySub?.unsubscribe();
        this.resetCategoriesSub?.unsubscribe();
    }

    showFeaturedPrograms = () => {
        this.data.tags.forEach((tag: SearchItem) => {
            if (tag.value.toLowerCase().trim() === 'featured') {
                tag.checked = true;
            }
        });

        const specialties = this.data.specialty.filter((val: SearchItem) => val.checked === true);
        const locations = this.data.locations.filter((val: SearchItem) => val.checked === true);
        const types = this.data.types.filter((val: SearchItem) => val.checked === true);
        const tags = this.data.tags.filter((val: SearchItem) => val.checked === true);
        const cities = this.data.cities.filter((val: SearchItem) => val.checked === true);
        const states = this.data.states.filter((val: SearchItem) => val.checked === true);

        const data = {
            specialty: specialties,
            locations,
            types,
            tags,
            cities,
            states,
        };

        this.store.dispatch(
            SearchActions.setSearchContext({
                context: 'Search for your next clinical experience with us. Featured',
            })
        );

        this.toolService.shareFilters.next(data);
    };

    showSuggestedPrograms = () => {
        this.store.dispatch(
            SearchActions.setSearchContext({
                context: 'Here are some suggested programs for you based on your preferences.',
            })
        );

        // this.store.dispatch(
        //     SearchActions.setShowSuggested({ suggested: true })
        // );

        this.programService.showSuggestedPrograms(this.user.id).subscribe((response: any) => {
            if (response.rows) {
                this.toolService.showSuggested.next([...response.rows]);
            }
        });
    };

    listeners() {
        // used to blank suggested if sort by is clicked
        this.toolService.getSearchSortValue().subscribe((sort: string) => {
            // when sort by is changed
            if (sort) {
                setTimeout(() => {
                    // get the list of current categories
                    this.categories.forEach((element) => {
                        // find the suggested category
                        if (element.title === 'View Your Suggested Programs') {
                            // set suggested category fill to solid
                            element.fill = 'solid';
                            // other categories use elastic search for results
                            // do not need to set their fills
                        }
                    });
                }, 500);
                this.lastClicked = null;
                // this.programService.setResetCatagoriesSubject.next(false);
            }
        });
    }

    setOnViewEnter() {
        //
        if (this.categories) {
            // clear all the chips
            Object.keys(this.categories).forEach((i) => (this.categories[i].fill = 'solid'));
            // set the lastClicked chip - don't forget to account for 0: suggested search
            if (this.lastClicked || this.lastClicked === 0) {
                this.categories[this.lastClicked].fill = 'outline';
            }
        }
    }

    getCategoryIcon(category: any): string {
        if (!category) return 'grid-outline';
        const q = category.queryObject || {};
        const title = (category.title || '').toLowerCase();
        if (q.suggested) return 'sparkles-outline';
        if (q.bundles) return 'layers-outline';
        if (title.includes('price drop')) return 'pricetag-outline';
        if (title.includes('virtual')) return 'laptop-outline';
        if (title.includes('academic')) return 'school-outline';
        if (title.includes('groups')) return 'people-outline';
        if (title.includes('inpatient')) return 'medkit-outline';
        if (title.includes('residency site')) return 'business-outline';
        if (title.includes('university program')) return 'school-outline';
        if (title.includes('hospital program')) return 'medkit-outline';
        if (title.includes('value')) return 'wallet-outline';
        if (title.includes('flexible')) return 'calendar-outline';
        if (title.includes('research')) return 'flask-outline';
        if (title.includes('last minute')) return 'flash-outline';
        return 'grid-outline';
    }

    getCategoryDisplayTitle(category: any): string {
        if (!category?.title) return '';
        if (this.isMobile && category.title === 'View Your Suggested Programs') {
            return 'Suggested Program';
        }
        return category.title;
    }

    clickCategory = (category: any, index: number) => {
        this.selection.emit({ selection: category, index: index });
        if (this.lastClicked === index) {
            this.lastClicked = null;
            this.categories[index].fill = 'solid';
            this.toolService.quickActionIndex.next(null);
            this.angulartics2GoogleTagManager.eventTrack('Category Deselected ' + this.categories[index].title, { category: 'Search' });
            this.reset();
        } else {
            // trigger GTM event
            this.angulartics2GoogleTagManager.eventTrack('Category Selected ' + this.categories[index].title, { category: 'Search' });

            // set all other category buttons to not selected state
            Object.keys(this.categories).forEach((i) => (this.categories[i].fill = 'solid'));
            // set current category button to selected state
            this.categories[index].fill = 'outline';

            // set last click to identify when unclicked
            this.lastClicked = index;
            // share category contents
            this.categoryButtonList = { ...category };
            // if this is the bundles button
            if (category.queryObject.bundles) {
                this.store.dispatch(
                    SearchActions.setShowProgramBundle({
                        showBundles: true,
                    })
                );
                this.store.dispatch(
                    SearchActions.setProgramBundleMapDisabled({
                        programBundleMapDisabled: true,
                    })
                );

                this.store.dispatch(
                    SearchActions.setSearchContext({
                        context: 'Program Bundles combine multiple programs to expand your experience and save you money!',
                    })
                );

                this.store.dispatch(
                    SearchActions.setShowSuggested({
                        suggested: false,
                    })
                );

                for (const k in this.categoryButtonList.queryObject) {
                    if (this.categoryButtonList.queryObject[k]) {
                        const t = {
                            checked: true,
                            value: this.categoryButtonList.queryObject[k],
                        };
                        this.categoryButtonList.queryObject[k] = typeof t.value === 'object' ? [t.value[0]] : [t];
                    }
                }
            } else if (category.queryObject.suggested) {
                for (const k in this.categoryButtonList.queryObject) {
                    if (this.categoryButtonList.queryObject[k]) {
                        const t = {
                            checked: true,
                            value: this.categoryButtonList.queryObject[k],
                        };
                        this.categoryButtonList.queryObject[k] = typeof t.value === 'object' ? [t.value[0]] : [t];
                    }
                }

                this.store.dispatch(
                    SearchActions.setShowProgramBundle({
                        showBundles: false,
                    })
                );

                this.showSuggestedPrograms();
            } else {
                this.store.dispatch(
                    SearchActions.setShowProgramBundle({
                        showBundles: false,
                    })
                );
                for (const k in this.categoryButtonList.queryObject) {
                    if (this.categoryButtonList.queryObject[k]) {
                        const t = {
                            checked: true,
                            value: this.categoryButtonList.queryObject[k],
                        };
                        this.categoryButtonList.queryObject[k] = typeof t.value === 'object' ? [t.value[0]] : [t];
                    }
                }
                // mock same format object as the drop down values
                // checked, value, display
                // only need to mock value and checked
                let formattedSearchProperty = {};
                for (const key in category.queryObject) {
                    let categoryObjectValue = category.queryObject[key];
                    let categoryValueString = categoryObjectValue[0].value;
                    formattedSearchProperty[key] = [{ value: categoryValueString, checked: true }];
                }
                this.toolService.quickActionIndex.next(index);
                this.reset(formattedSearchProperty);
            }
        }
    };

    reset(extraParam?: object) {
        this.store.dispatch(
            SearchActions.setSearchContext({
                context: 'Search for your next clinical experience with us.',
            })
        );

        // hack to reset all filters
        const data = {
            specialty: [],
            types: [],
            tags: [],
            cities: [],
            states: [],
            locations: [],
            dates: [],
            ...extraParam,
        };
        //
        this.toolService.shareFilters.next(data);
    }

    /**
     *
     * @param event mouse event, use coordinates to build template on
     * @param selectedTab string value of selected tab
     */
    open({ x, y }: MouseEvent, category: any, index: number) {
        this.angulartics2GoogleTagManager.eventTrack('Category Selected ' + this.categories[index].title, { category: 'Search' });

        this.lastClicked = index;

        const url = this.router
            .createUrlTree([`/search`], {
                relativeTo: this.route,
                queryParams: category.queryObject,
            })
            .toString();

        // this try catch for event might not work later
        // need another way to add to params for event
        try {
            // eslint-disable-next-line import/no-deprecated
            event.preventDefault();
        } catch (err) {
            throw err;
        }

        this.close();
        // setup for context menu template
        const positionStrategy = this.overlay
            .position()
            .flexibleConnectedTo({ x, y })
            .withPositions([
                {
                    originX: 'end',
                    originY: 'bottom',
                    overlayX: 'end',
                    overlayY: 'top',
                },
            ]);

        this.overlayRef = this.overlay.create({
            positionStrategy,
            scrollStrategy: this.overlay.scrollStrategies.close(),
        });
        // use ng template reference and attach tab string to it
        this.overlayRef.attach(
            new TemplatePortal(this.navMenu, this.viewContainerRef, {
                $implicit: url,
            })
        );

        this.sub = fromEvent<MouseEvent>(document, 'click')
            .pipe(
                filter((event) => {
                    const clickTarget = event.target as HTMLElement;
                    return !!this.overlayRef && !this.overlayRef.overlayElement.contains(clickTarget);
                }),
                take(1)
            )
            .subscribe(() => this.close());
    }

    /**
     * closes context menu
     */
    close() {
        this.sub && this.sub.unsubscribe();
        if (this.overlayRef) {
            this.overlayRef.dispose();
            this.overlayRef = null;
        }
    }

    /**
     *
     * @param tab sting value for tab selected by right click context menu
     *
     */
    openNewTab(tab: string) {
        const url = window.location.origin;
        // no backslash cause url tree creates it for you
        window.open(`${url}${tab}`, '_blank');
        this.close();
    }
}
